/**
 * useEntitlementsCatalog — GET /api/entitlements/catalog (plans + add-ons for
 * the tenant's region). Used by the Upgrade dialog and the Plan & Billing
 * page. Cached 5 min client-side on top of the backend's own 5 min
 * in-process cache — the catalog changes only when VentureSoft staff edit
 * pricing/tier data in the corp portal.
 */
'use client'

import { useQuery } from '@tanstack/react-query'

import type { FeatureKey } from '@shared/entitlements'

import { api } from '@/lib/api'

export type CatalogPlan = {
  id: string
  name: string
  planKind: 'solo' | 'team' | 'internal'
  tierRank: number | null
  maxUsers: number | null
  priceMonthly: number | null
  priceAnnual: number | null
  currency: string | null
  extraSeatPrice: number | null
  allowsExtraSeats: boolean
  featureKeys: FeatureKey[]
}

export type CatalogAddon = {
  key: string
  name: string
  description: string | null
  grantsFeatureKey: FeatureKey
  priceMonthly: number | null
  priceAnnual: number | null
  currency: string | null
}

export type Catalog = { plans: CatalogPlan[]; addons: CatalogAddon[] }

export const CATALOG_QUERY_KEY = ['entitlements', 'catalog'] as const
export const CATALOG_STALE_TIME = 5 * 60 * 1000

export function useEntitlementsCatalog(options?: { enabled?: boolean }) {
  return useQuery<Catalog>({
    queryKey: CATALOG_QUERY_KEY,
    queryFn: () => api.get<Catalog>('/api/entitlements/catalog'),
    staleTime: CATALOG_STALE_TIME,
    enabled: options?.enabled,
  })
}
